// @flow

import ReactGA from 'react-ga';

import React, { Component } from 'react';
import * as d3 from 'd3';

import { topoToGeo } from './DataManipulation';

import { calculateClimateColor } from './App/ClimateZoneColors';
import { PROJECTION } from './App/Constants';
import { mobile } from './App/Mobile';

import {
  earningsSliderFormater,
  priceSliderFormater,
  percentFormater,
  populationSliderFormater,
  populationFormater,
} from './App/Formatters';

import { addIfTrue, filterByPredicates, isInRange, isOverlappingRange } from './App/Predicates';

import { initialState } from './App/State';
import { mapData } from './App/DataMapper';

import {
  ColorScaleLegend,
  LocatedCircles,
  Map,
  MeasureSelector,
  Popup,
  Pulldown,
  RadioButton,
  RadioButtonGroup,
  ScalableLetterbox,
  ScatterPlot,
  Slider,
  TextBox,
  Tooltip,
  ZoomableGroup,
} from './components';

import { calculateArea, calculateLocation } from './App/Calculations';

import CollegeDetails from './App/CollegeDetails';

import { buildLocationTooltip, buildRegionTooltip } from './App/TooltipBuilders';

import './App.css';

const noFill = d => 'none';
const neutralFill = d => '#EEEEEE';
const mobileFill = d => '#F9F9F4';
const blackStroke = d => 'black';
const collegesKeyFunction = d => d.OPEID;
const statesKeyFunction = d => d.id;
const thickStrokeWidth = d => 0.7;
const thinStrokeWidth = d => 0.45;
const transparent = d => 0;

class App extends Component {
  state: Object;
  onFilterChange: Function;
  onCollegeDeselection: Function;
  onCollegeSelection: Function;
  onDependentMeasureChange: Function;
  onIndependentMeasureChange: Function;
  onMeasureChange: Function;
  renderApp: Function;
  renderWait: Function;

  constructor(props: Object) {
    super(props);
    this.onFilterChange = this.onFilterChange.bind(this);
    this.onCollegeDeselection = this.onCollegeDeselection.bind(this);
    this.onCollegeSelection = this.onCollegeSelection.bind(this);
    this.onIndependentMeasureChange = this.onIndependentMeasureChange.bind(this);
    this.onDependentMeasureChange = this.onDependentMeasureChange.bind(this);
    this.onMeasureChange = this.onMeasureChange.bind(this);
    this.renderApp = this.renderApp.bind(this);
    this.renderWait = this.renderWait.bind(this);

    this.state = initialState;

    ReactGA.initialize('UA-112436133-1');
    ReactGA.pageview(window.location.pathname + window.location.search);
  }

  componentDidMount() {
    const basePath = process.env.REACT_APP_BASEPATH || '';
    const cacheBuster = process.env.REACT_APP_CACHEBUSTER || '1234';

    d3
      .queue()
      .defer(d3.json, `${basePath}/topo-json/us-10m.json?cacheBuster=${cacheBuster}`)
      .defer(d3.csv, `${basePath}/data/narrow.csv?cacheBuster=${cacheBuster}`)
      .defer(d3.csv, `${basePath}/data/cipNames.csv?cacheBuster=${cacheBuster}`)
      .await((error, topoJSON, rawColleges, cipNames) => {
        if (error) {
          console.log('error', error);
          return;
        }

        const statesGeoJSON = topoToGeo(topoJSON, 'states');
        const countiesGeoJSON = topoToGeo(topoJSON, 'counties');

        const colleges = rawColleges.map(mapData);

        const programFilter = this.state.programFilter;
        programFilter.options = cipNames.sort((p1, p2) => {
          return p1.name < p2.name ? -1 : 1;
        });

        this.setState({
          colleges,
          statesGeoJSON,
          countiesGeoJSON,
          programFilter,
          loading: false,
        });
      });
  }

  onFilterChange({ filterName, enabled, value }: Object) {
    const currentState = this.state[filterName];
    const enablementChanged = filterName && currentState.enabled !== enabled;

    currentState.enabled = enabled;
    currentState.value = value;

    const holder = {};
    holder[filterName] = currentState;
    this.setState(holder);

    if (enablementChanged) {
      const action = enabled ? 'Enabled' : 'Disabled';
      ReactGA.event({
        category: 'Filter',
        action,
        label: filterName,
      });
    }
  }

  onMeasureChange({ value }: Object) {
    const measure = this.state.measures.filter(m => m.key === value)[0];
    this.setState({ selectedMeasure: measure });

    ReactGA.event({
      category: 'Measure',
      action: 'Selected',
      label: measure.label,
    });
  }

  onIndependentMeasureChange({ value }: Object) {
    const measure = this.state.measures.filter(m => m.key === value)[0];
    this.setState({ selectedIndependentMeasure: measure });

    ReactGA.event({
      category: 'Independent Measure',
      action: 'Selected',
      label: measure.label,
    });
  }

  onDependentMeasureChange({ value }: Object) {
    const measure = this.state.measures.filter(m => m.key === value)[0];
    this.setState({ selectedDependentMeasure: measure });

    ReactGA.event({
      category: 'Dependent Measure',
      action: 'Selected',
      label: measure.label,
    });
  }

  onCollegeSelection(college: Object) {
    if (this.state.selectedCollege === college) {
      this.setState({ selectedCollege: null });
    } else {
      this.setState({ selectedCollege: college });
    }
  }

  onCollegeDeselection() {
    this.setState({ selectedCollege: null });
  }

  renderApp() {
    const lastUpdate = this.state.lastUpdate || 0;
    const byHighDegreePredicate = d => {
      if (+this.state.highDegreeFilter.value === 2) {
        return +d.HIGHDEG === 2;
      }

      return +d.HIGHDEG >= +this.state.highDegreeFilter.value;
    };

    // overlapping sat ranges
    const bySatPredicate = d => {
      return isOverlappingRange(this.state.satFilter.value, {
        min: d.satCombined25,
        max: d.satCombined75,
      });
    };

    // overlapping act ranges
    const byActPredicate = d => {
      return isOverlappingRange(this.state.actFilter.value, {
        min: d.ACTCM25,
        max: d.ACTCM75,
      });
    };

    const byNamePredicate = d => {
      return (
        this.state.nameFilter.value === '' ||
        d.INSTNM.toLowerCase().includes(this.state.nameFilter.value.toLowerCase().trim())
      );
    };

    const byPopulationPredicate = d => {
      return isInRange(
        this.state.populationFilter.value,
        this.state.populationFilter.limits,
        d.UGDS,
        true,
      );
    };

    const byAdmissionRatePredicate = d => {
      return isInRange(
        this.state.admissionRateFilter.value,
        this.state.admissionRateFilter.limits,
        d.ADM_RATE,
        true,
      );
    };

    const byOntimeCompletionPredicate = d => {
      return isInRange(
        this.state.ontimeCompletionFilter.value,
        this.state.ontimeCompletionFilter.limits,
        d.onTimeCompletionRate,
        true,
      );
    };

    const byExtraTimeCompletionPredicate = d => {
      return isInRange(
        this.state.extraTimeCompletionFilter.value,
        this.state.extraTimeCompletionFilter.limits,
        d.extraTimeCompletionRate,
        true,
      );
    };

    const byRepaymentAtSevenPredicate = d => {
      return isInRange(
        this.state.repaymentAtSevenFilter.value,
        this.state.repaymentAtSevenFilter.limits,
        d.RPY_7YR_RT,
        true,
      );
    };

    const byEarningsAtTenPredicate = d => {
      return isInRange(
        this.state.earningsAtTenFilter.value,
        this.state.earningsAtTenFilter.limits,
        d.MN_EARN_WNE_P10,
        true,
      );
    };

    const byNetPricePredicate = d => {
      return isInRange(
        this.state.netPriceFilter.value,
        this.state.netPriceFilter.limits,
        d.netPrice,
        true,
        true,
      );
    };

    const byInStateTuitionPredicate = d => {
      return isInRange(
        this.state.inStateTuitionFilter.value,
        this.state.inStateTuitionFilter.limits,
        d.TUITIONFEE_IN,
        true,
        true,
      );
    };

    const byOutOfStateTuitionPredicate = d => {
      return isInRange(
        this.state.outOfStateTuitionFilter.value,
        this.state.outOfStateTuitionFilter.limits,
        d.TUITIONFEE_OUT,
        true,
        true,
      );
    };

    const byProgramPredicate = d => {
      const { value } = this.state.programFilter;
      if (!value || value === -1) {
        return true;
      }

      const bachelorsKey = `CIP${value}BACHL`;
      const associatesKey = `CIP${value}ASSOC`;

      const bachelorsValue = +d[bachelorsKey];
      const associatesValue = +d[associatesKey];

      return bachelorsValue > 0 || associatesValue > 0;
    };

    const predicates = [];
    addIfTrue(predicates, this.state.actFilter.enabled, byActPredicate);
    addIfTrue(predicates, this.state.highDegreeFilter.enabled, byHighDegreePredicate);
    addIfTrue(predicates, this.state.nameFilter.enabled, byNamePredicate);
    addIfTrue(predicates, this.state.netPriceFilter.enabled, byNetPricePredicate);
    addIfTrue(predicates, this.state.inStateTuitionFilter.enabled, byInStateTuitionPredicate);
    addIfTrue(predicates, this.state.outOfStateTuitionFilter.enabled, byOutOfStateTuitionPredicate);
    addIfTrue(predicates, this.state.ontimeCompletionFilter.enabled, byOntimeCompletionPredicate);
    addIfTrue(predicates, this.state.admissionRateFilter.enabled, byAdmissionRatePredicate);

    addIfTrue(
      predicates,
      this.state.extraTimeCompletionFilter.enabled,
      byExtraTimeCompletionPredicate,
    );
    addIfTrue(predicates, this.state.repaymentAtSevenFilter.enabled, byRepaymentAtSevenPredicate);
    addIfTrue(predicates, this.state.earningsAtTenFilter.enabled, byEarningsAtTenPredicate);
    addIfTrue(predicates, this.state.populationFilter.enabled, byPopulationPredicate);
    addIfTrue(predicates, this.state.programFilter.enabled, byProgramPredicate);
    addIfTrue(predicates, this.state.satFilter.enabled, bySatPredicate);

    const filteredColleges = filterByPredicates(this.state.colleges, predicates);
    const { selectedMeasure } = this.state;

    const independentMeasures = this.state.measures.filter(m => m.independentVariable);
    const dependentMeasures = this.state.measures.filter(m => !m.independentVariable);

    return (
      <div className="App">
        <Tooltip />
        <div className="top">
          <div className="title">US Colleges</div>
          <div className="scale">
            <MeasureSelector
              label="College Color based on"
              measures={this.state.measures}
              selectedMeasure={this.state.selectedMeasure}
              onMeasureChange={this.onMeasureChange}
            />
          </div>
          <ColorScaleLegend {...selectedMeasure} />
        </div>
        <div className="content">
          <div className="filters">
            <div className="section-heading">control which colleges are shown</div>
            <div className="counts">
              Showing <span className="count">{populationFormater(filteredColleges.length)}</span>{' '}
              colleges out of{' '}
              <span className="count">{populationFormater(this.state.colleges.length)}</span>
            </div>
            <RadioButtonGroup
              label="Degree Type"
              onFilterChange={this.onFilterChange}
              {...this.state.highDegreeFilter}>
              <RadioButton label="Associates" value="2" />
              <RadioButton label="Bachelors" value="3" />
              <RadioButton label="Graduate" value="4" />
            </RadioButtonGroup>
            <Slider
              label="Size (Undergraduate Students)"
              formatLabel={populationSliderFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.populationFilter}
            />
            <TextBox
              label="School Name"
              onFilterChange={this.onFilterChange}
              {...this.state.nameFilter}
            />
            <Slider
              label="Admission Rate"
              formatLabel={percentFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.admissionRateFilter}
            />
            <Slider
              label="Ontime Completion Rate"
              formatLabel={percentFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.ontimeCompletionFilter}
            />
            <Slider
              label="Extra Time Completion Rate"
              formatLabel={percentFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.extraTimeCompletionFilter}
            />
            <Slider
              label="Earnings 10 Years after Enrollment"
              formatLabel={earningsSliderFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.earningsAtTenFilter}
            />
            <Slider
              label="Repayment Progress 7 Years Later"
              formatLabel={percentFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.repaymentAtSevenFilter}
            />
            <Slider
              label="SAT Range"
              onFilterChange={this.onFilterChange}
              {...this.state.satFilter}
            />
            <Slider
              label="ACT Range"
              onFilterChange={this.onFilterChange}
              {...this.state.actFilter}
            />
            <Slider
              label="Net Price"
              formatLabel={priceSliderFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.netPriceFilter}
            />
            <Slider
              label="In State Tuition"
              formatLabel={priceSliderFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.inStateTuitionFilter}
            />
            <Slider
              label="Out of State Tuition"
              formatLabel={priceSliderFormater}
              onFilterChange={this.onFilterChange}
              {...this.state.outOfStateTuitionFilter}
            />
            <Pulldown
              label="Program"
              onFilterChange={this.onFilterChange}
              {...this.state.programFilter}
            />
          </div>
          <div className="data">
            <ScalableLetterbox desiredWidth={960 / 5} desiredHeight={600 / 5}>
              <ZoomableGroup width={960 / 5} height={600 / 5}>
                <Map
                  regionsGeoJSON={this.state.countiesGeoJSON}
                  buildTooltip={buildRegionTooltip}
                  calculateFill={calculateClimateColor}
                  calculateStroke={calculateClimateColor}
                  calculateStrokeWidth={thinStrokeWidth}
                  keyFunction={statesKeyFunction}
                  lastUpdate={lastUpdate}
                  mobile={mobile}
                  minScale="0"
                  maxScale="1000000"
                  projection={PROJECTION}
                />
                <Map
                  regionsGeoJSON={this.state.statesGeoJSON}
                  buildTooltip={buildRegionTooltip}
                  calculateFill={mobile ? mobileFill : neutralFill}
                  calculateFillOpacity={mobile ? 1 : transparent}
                  calculateStroke={blackStroke}
                  calculateStrokeWidth={thickStrokeWidth}
                  keyFunction={statesKeyFunction}
                  lastUpdate={lastUpdate}
                  minScale="0"
                  maxScale={mobile ? 1000000 : 5}
                  projection={PROJECTION}
                />
                <Map
                  regionsGeoJSON={this.state.countiesGeoJSON}
                  buildTooltip={buildRegionTooltip}
                  calculateFill={neutralFill}
                  calculateFillOpacity={transparent}
                  calculateStroke={blackStroke}
                  calculateStrokeWidth={thinStrokeWidth}
                  keyFunction={statesKeyFunction}
                  lastUpdate={lastUpdate}
                  mobile={mobile}
                  minScale="5"
                  maxScale="1000000"
                  projection={PROJECTION}
                />
                <Map
                  regionsGeoJSON={this.state.statesGeoJSON}
                  buildTooltip={buildRegionTooltip}
                  calculateFill={noFill}
                  calculateFillOpacity={transparent}
                  calculateStroke={blackStroke}
                  calculateStrokeWidth={thickStrokeWidth}
                  keyFunction={statesKeyFunction}
                  lastUpdate={lastUpdate}
                  mobile={mobile}
                  minScale="5"
                  maxScale="1000000"
                  projection={PROJECTION}
                />
                <LocatedCircles
                  buildTooltip={d => buildLocationTooltip(d, selectedMeasure)}
                  calculateArea={calculateArea}
                  calculateFill={selectedMeasure.calculateFill}
                  calculateLocation={calculateLocation}
                  calculateStroke={blackStroke}
                  calculateStrokeWidth={thinStrokeWidth}
                  locations={filteredColleges}
                  keyFunction={collegesKeyFunction}
                  lastUpdate={lastUpdate}
                  onSelection={this.onCollegeSelection}
                  scale="1"
                />
              </ZoomableGroup>
            </ScalableLetterbox>
            <ScalableLetterbox desiredWidth={960 / 5} desiredHeight={600 / 5}>
              <ZoomableGroup width={960 / 5} height={600 / 5}>
                <ScatterPlot
                  xMeasure={this.state.selectedIndependentMeasure}
                  yMeasure={this.state.selectedDependentMeasure}
                  width={960 / 5}
                  height={600 / 5}
                  buildTooltip={d => buildLocationTooltip(d, selectedMeasure)}
                  calculateArea={calculateArea}
                  data={filteredColleges}
                  keyFunction={collegesKeyFunction}
                  calculateFill={selectedMeasure.calculateFill}
                  calculateStroke={blackStroke}
                  calculateStrokeWidth={thinStrokeWidth}
                  onSelection={this.onCollegeSelection}
                  scale="1"
                />
              </ZoomableGroup>
            </ScalableLetterbox>
            <MeasureSelector
              label="Independent Variable"
              measures={independentMeasures}
              selectedMeasure={this.state.selectedIndependentMeasure}
              onMeasureChange={this.onIndependentMeasureChange}
            />
            <MeasureSelector
              label="Dependent Variable"
              measures={dependentMeasures}
              selectedMeasure={this.state.selectedDependentMeasure}
              onMeasureChange={this.onDependentMeasureChange}
            />
          </div>
        </div>
        {this.state.selectedCollege && (
          <Popup onClose={this.onCollegeDeselection}>
            <CollegeDetails college={this.state.selectedCollege} />
          </Popup>
        )}
      </div>
    );
  }

  renderWait() {
    return (
      <div className="App">
        <div id="waiting">
          <div>Waiting for Data</div>
        </div>
      </div>
    );
  }

  render() {
    return this.state.loading ? this.renderWait() : this.renderApp();
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js